import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, getToken, api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Send, Sparkles, Loader2, Shield, History, FileText, X, Plus } from "lucide-react";
import { AIText } from "@/components/AIText";
import { DocPicker } from "@/components/DocPicker";

export default function Chat() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [contextDoc, setContextDoc] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const scrollRef = useRef();
  const started = useRef(false);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, streaming]);

  useEffect(() => {
    api.get("/ai/context").then((r) => { if (r.data.document_id) setContextDoc(r.data); }).catch(() => {});
  }, []);

  const clearContext = async () => { setContextDoc(null); try { await api.delete("/ai/context"); } catch {} };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (location.state?.sessionId) {
      const sid = location.state.sessionId;
      setSessionId(sid);
      api.get(`/ai/conversations/${sid}`).then((r) =>
        setMessages(r.data.map((m) => ({ role: m.role, content: m.content })))
      ).catch(() => {});
    } else if (location.state?.initial) {
      send(location.state.initial);
    }
  }, []); // eslint-disable-line

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: msg, language: lang, session_id: sessionId, document_id: contextDoc?.document_id || null }),
      });
      const sid = res.headers.get("X-Session-Id");
      if (sid && !sessionId) setSessionId(sid);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: t("chat_error") }; return c; });
    } finally { setStreaming(false); }
  };

  const suggestions = [t("q1"), t("q2"), t("q3"), t("explain_deductible_15")];

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl flex flex-col" style={{ height: "calc(100vh - 130px)" }}>
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl brand-gradient text-white"><Sparkles className="h-5 w-5" /></span>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-semibold text-navy">{t("ask_ai")}</h1>
            <p className="text-xs text-slate-400">{user?.state ? t("chat_answers_state", { state: user.state }) : t("chat_independent")}</p>
          </div>
          <button data-testid="chat-history-btn" onClick={() => navigate("/conversations")}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-navy hover:bg-slate-50">
            <History className="h-4 w-4" /> <span className="hidden sm:inline">{t("history")}</span>
          </button>
        </div>

        {/* Context (memory recall) */}
        <div className="mt-3">
          {contextDoc ? (
            <div data-testid="chat-context-chip" className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-3 py-1.5 text-sm text-brandteal">
              <FileText className="h-4 w-4" />
              <span className="font-medium">{t("using")}: {contextDoc.name}{contextDoc.insurance_type ? ` (${contextDoc.insurance_type})` : ""}</span>
              <button data-testid="chat-context-clear" onClick={clearContext} className="ml-1 rounded-full hover:bg-teal-100 p-0.5"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button data-testid="chat-context-add" onClick={() => setShowPicker((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:border-brandblue hover:text-brandblue">
              <Plus className="h-4 w-4" /> {t("ask_specific_policy")}
            </button>
          )}
          {showPicker && !contextDoc && (
            <div className="mt-2 max-w-sm">
              <DocPicker testId="chat-context-picker" value="" placeholder={t("choose_document")}
                onChange={async (id) => {
                  setShowPicker(false);
                  try { const r = await api.post("/ai/context", { document_id: id }); setContextDoc(r.data); } catch {}
                }} />
            </div>
          )}
        </div>

        <div ref={scrollRef} className="mt-4 flex-1 overflow-y-auto space-y-4 pr-1">
          {messages.length === 0 && (
            <div className="mt-6">
              <p className="text-center text-slate-400 text-sm mb-4">{t("try_asking")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {suggestions.map((s, i) => (
                  <button key={i} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
                    className="rounded-2xl border border-slate-100 bg-white card-shadow px-4 py-3 text-left text-sm font-medium text-navy hover:-translate-y-0.5 transition-transform">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <span className="mr-2 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-brandblue"><Shield className="h-4 w-4" /></span>}
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "brand-gradient text-white" : "bg-white border border-slate-100 text-slate-700"}`}>
                {m.content ? (m.role === "assistant" ? <AIText>{m.content}</AIText> : <p className="whitespace-pre-wrap">{m.content}</p>) : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2 rounded-2xl bg-white border border-slate-100 card-shadow p-2">
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("ask_placeholder")}
            className="flex-1 bg-transparent px-3 py-2 text-navy focus:outline-none" />
          <Button data-testid="chat-send" type="submit" disabled={streaming} className="rounded-xl brand-gradient text-white px-4"><Send className="h-4 w-4" /></Button>
        </form>
      </div>
    </AppLayout>
  );
}
